import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect } from 'react';

import { ArrowCircleLeft } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  queryKeys,
  useChangePaymentMethodSetupIntent,
  useStripeSubscription,
} from 'src/api/queries';
import {
  Button,
  Modal,
  NotificationBox,
  Spinner,
  WithStripeElements,
} from 'src/components';
import { subscriptionHasPaymentMethod } from 'src/utils/subscriptionPayment';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import { EditPaymentMethod } from './EditPaymentMethod';

interface EditPaymentMethodWrapperProps {
  subscriptionId?: string;
}

const EditPaymentMethodWrapper = ({
  subscriptionId,
  componentLocation,
}: EditPaymentMethodWrapperProps & { componentLocation: string }) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const changePaymentMethodSetupIntent = useChangePaymentMethodSetupIntent();

  useEffect(() => {
    if (!subscriptionId) return;
    changePaymentMethodSetupIntent.mutate(
      { subscriptionId },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(
            queryKeys.subscriptions.createSetupIntent,
            data
          );
        },
      }
    );
  }, [subscriptionId]);

  if (!subscriptionId) return <Spinner />;

  if (changePaymentMethodSetupIntent.isError) {
    return (
      <>
        <NotificationBox
          title={t`Something went wrong`}
          data-cy="edit-payment-method-create-setup-intent-failed"
          error={changePaymentMethodSetupIntent.error}
        >
          <Typography variant="body-sm" as="p">
            <Trans>
              Please try again, or{' '}
              <a href={SUPPORT_URL.HOME} target="_blank" rel="noreferrer">
                contact support
              </a>{' '}
              if the problem persists. We are sorry about the inconvenience.
            </Trans>
          </Typography>
        </NotificationBox>

        <Modal.Close asChild>
          <Button
            variant="secondary"
            analytics={{
              text: 'Back',
              location: ComponentLocations.MANAGE_CONNECT.MAIN,
              action: 'close edit payment method modal',
            }}
          >
            <ArrowCircleLeft />
            <span>
              <Trans>Back</Trans>
            </span>
          </Button>
        </Modal.Close>
      </>
    );
  }

  if (
    changePaymentMethodSetupIntent.isPending ||
    !changePaymentMethodSetupIntent.data?.clientSecret
  ) {
    return <Spinner />;
  }

  const options: StripeElementsOptions = {
    clientSecret: changePaymentMethodSetupIntent.data.clientSecret,
  };

  return (
    <WithStripeElements options={options}>
      <EditPaymentMethod
        setupIntent={changePaymentMethodSetupIntent}
        subscriptionId={subscriptionId}
        componentLocation={componentLocation}
      />
    </WithStripeElements>
  );
};

export const EditPaymentMethodModal = ({
  componentLocation,
}: {
  componentLocation: string;
}) => {
  const { t } = useLingui();
  const subscription = useStripeSubscription();

  return (
    <Modal.Content
      title={`${
        subscriptionHasPaymentMethod(subscription.data)
          ? t`Change payment method`
          : t`Add payment method`
      }`}
    >
      <EditPaymentMethodWrapper
        subscriptionId={subscription.data?.id}
        componentLocation={componentLocation}
      />
    </Modal.Content>
  );
};
